// slots.ts - Фінансово-операційний трекер слотів та цілі 40 000 грн

interface SlotDayReport {
    slot_date: string;
    pum_hours: number;
    school_hours: number;
    delivery_hours: number;
    delivery_earnings: number;
    sleep_maintained: number;
    notes: string;
}

interface SlotsSummary {
    today: SlotDayReport;
    week: {
        monday: string;
        sunday: string;
        delivery_hours: number;
        delivery_quota: number;
        delivery_debt: number;
        delivery_earnings: number;
        avg_rate: number;
        target_rate: number;
        is_quota_met: boolean;
    };
    month: {
        month_name: string;
        target_income: number;
        school_base: number;
        pum_base: number;
        delivery_base?: number;
        school_actual?: number;
        pum_actual?: number;
        other_income?: number;
        delivery_earnings: number;
        delivery_hours: number;
        total_income: number;
        remaining: number;
        progress_pct: number;
    };
    settings: {
        target_income: number;
        school_base: number;
        pum_base: number;
        delivery_base?: number;
        delivery_quota_weekly: number;
        delivery_rate_target: number;
    };
    history: SlotDayReport[];
}

let slotsData: SlotsSummary | null = null;
let slotsInterval: any = null;

const SCHEDULE_WEEKDAY = [
    { start: '00:00', end: '09:00', name: 'Залізне вікно сну (недоторканне)', type: 'sleep', icon: 'ri-moon-clear-line', color: '#8b5cf6' },
    { start: '09:00', end: '10:30', name: 'Підйом, ранок, сніданок, побут', type: 'free', icon: 'ri-sun-line', color: '#3b82f6' },
    { start: '10:30', end: '11:00', name: 'Дорога до ПУМ', type: 'commute', icon: 'ri-walk-line', color: '#64748b' },
    { start: '11:00', end: '12:30', name: 'ПУМ [Слот Робота-1]', type: 'work', icon: 'ri-macbook-line', color: '#0ea5e9' },
    { start: '12:30', end: '13:00', name: 'Обід', type: 'break', icon: 'ri-restaurant-line', color: '#10b981' },
    { start: '13:00', end: '13:30', name: 'Дорога до ліцею', type: 'commute', icon: 'ri-walk-line', color: '#64748b' },
    { start: '13:30', end: '18:00', name: 'Ліцей (інклюзія) [Слот Робота-2]', type: 'work', icon: 'ri-book-read-line', color: '#f59e0b' },
    { start: '18:00', end: '18:30', name: 'Перехід у точку старту замовлень', type: 'commute', icon: 'ri-e-bike-2-line', color: '#64748b' },
    { start: '18:30', end: '22:30', name: 'Доставка (Glovo + Bolt) [Слот Доставка · 4 год]', type: 'delivery', icon: 'ri-riding-line', color: '#ec4899' },
    { start: '22:30', end: '24:00', name: 'Повернення, вечеря, душ, особистий час', type: 'free', icon: 'ri-home-4-line', color: '#3b82f6' },
];

// Розклад на суботу: 5 годин суцільно у пікові вечірні години доставки (17:00–22:00)
const SCHEDULE_SATURDAY = [
    { start: '00:00', end: '09:00', name: 'Залізне вікно сну (недоторканне)', type: 'sleep', icon: 'ri-moon-clear-line', color: '#8b5cf6' },
    { start: '09:00', end: '17:00', name: 'Субота: Відпочинок, побут & відновлення', type: 'free', icon: 'ri-sun-line', color: '#3b82f6' },
    { start: '17:00', end: '22:00', name: 'Доставка (Glovo + Bolt) [Суботній піковий слот · 5 год суцільно]', type: 'delivery', icon: 'ri-riding-line', color: '#ec4899' },
    { start: '22:00', end: '24:00', name: 'Повернення, вечеря, душ, підготовка до сну', type: 'free', icon: 'ri-home-4-line', color: '#3b82f6' },
];

// Розклад на неділю: повний вихідний день (буфер відновлення)
const SCHEDULE_SUNDAY = [
    { start: '00:00', end: '09:00', name: 'Залізне вікно сну (недоторканне)', type: 'sleep', icon: 'ri-moon-clear-line', color: '#8b5cf6' },
    { start: '09:00', end: '24:00', name: 'Неділя: Повний вихідний (Буфер відновлення)', type: 'weekend', icon: 'ri-cup-line', color: '#10b981' },
];

function getCurrentSlotInfo() {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun, 6 = Sat
    const currentMins = now.getHours() * 60 + now.getMinutes();

    let activeSchedule = SCHEDULE_WEEKDAY;
    if (day === 6) {
        activeSchedule = SCHEDULE_SATURDAY;
    } else if (day === 0) {
        activeSchedule = SCHEDULE_SUNDAY;
    }

    for (let slot of activeSchedule) {
        const [sh, sm] = slot.start.split(':').map(Number);
        const [eh, em] = slot.end === '24:00' ? [24, 0] : slot.end.split(':').map(Number);
        const startMins = sh * 60 + sm;
        const endMins = eh * 60 + em;

        if (currentMins >= startMins && currentMins < endMins) {
            const diffMins = endMins - currentMins;
            const diffH = Math.floor(diffMins / 60);
            const diffM = diffMins % 60;
            const timeStr = diffH > 0 ? `${diffH} год ${diffM} хв` : `${diffM} хв`;

            return {
                isWeekend: day === 0,
                name: slot.name,
                type: slot.type,
                icon: slot.icon,
                color: slot.color,
                start: slot.start,
                end: slot.end,
                desc: `Триває з ${slot.start} до ${slot.end} · ${day === 6 ? 'Суботній графік (5 год пік)' : day === 0 ? 'Вихідний буфер' : 'Будній графік (4 год)'}`,
                timeUntilNext: `Залишилося: ${timeStr}`
            };
        }
    }

    return {
        isWeekend: day === 0,
        name: day === 0 ? 'Недільний буфер відновлення' : 'Графік дня',
        type: 'free',
        icon: 'ri-time-line',
        color: '#64748b',
        desc: 'Поточний слот',
        timeUntilNext: ''
    };
}

async function loadSlotsSummary() {
    const refreshBtn = document.getElementById('slots-btn-refresh');
    const refreshIcon = refreshBtn?.querySelector('i');
    if (refreshIcon) refreshIcon.classList.add('bb-spin');

    try {
        const res = await fetch('api/slots.php?action=summary');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success) {
            slotsData = data;
            renderSlotsUI();
        }
    } catch (e) {
        console.error('Failed to load slots summary:', e);
    } finally {
        if (refreshIcon) {
            setTimeout(() => refreshIcon.classList.remove('bb-spin'), 350);
        }
    }
}

function syncDeliveryWidgetFromPlanner() {
    if (!slotsData) return;
    const week = slotsData.week;
    const quota = week.delivery_quota || 25;

    // Планові години з щотижневого розкладу
    const plannedHours = typeof (window as any).getPlannedDeliveryHours === 'function'
        ? Number((window as any).getPlannedDeliveryHours())
        : (week.delivery_hours || 25);

    const weekHoursEl = document.getElementById('slots-week-hours');
    const weekQuotaEl = document.getElementById('slots-week-quota');
    const weekProgressFillEl = document.getElementById('slots-week-progress-fill');
    const weekStatusBadgeEl = document.getElementById('slots-week-status-badge');
    const weekPctEl = document.getElementById('slots-week-pct');

    if (weekHoursEl) weekHoursEl.textContent = `${plannedHours.toFixed(1)} год`;
    if (weekQuotaEl) weekQuotaEl.textContent = `з ${quota.toFixed(1)} год`;
    
    const plannedPct = Math.min(100, Math.round((plannedHours / quota) * 100));
    if (weekPctEl) weekPctEl.textContent = `${plannedPct}%`;
    if (weekProgressFillEl) weekProgressFillEl.style.width = `${plannedPct}%`;

    if (weekStatusBadgeEl) {
        if (plannedHours >= quota) {
            weekStatusBadgeEl.className = 'slots-badge badge-success';
            weekStatusBadgeEl.innerHTML = `<i class="ri-checkbox-circle-line"></i> Заповнено ${plannedHours.toFixed(1)}/${quota}г!`;
        } else {
            const diff = (quota - plannedHours).toFixed(1);
            weekStatusBadgeEl.className = 'slots-badge badge-warning';
            weekStatusBadgeEl.innerHTML = `<i class="ri-alarm-warning-line"></i> Не вистачає ${diff} год`;
        }
    }
}
(window as any).syncDeliveryWidgetFromPlanner = syncDeliveryWidgetFromPlanner;

function renderSlotsUI() {
    if (!slotsData) return;

    // 1. Live Slot Banner
    const current = getCurrentSlotInfo();
    const liveIconEl = document.getElementById('slots-live-icon');
    const liveTitleEl = document.getElementById('slots-live-title');
    const liveDescEl = document.getElementById('slots-live-desc');
    const liveCountdownEl = document.getElementById('slots-live-countdown');

    if (liveIconEl) {
        liveIconEl.className = `${current.icon} slots-live-icon`;
        liveIconEl.style.color = current.color;
    }
    if (liveTitleEl) liveTitleEl.textContent = current.name;
    if (liveDescEl) liveDescEl.textContent = current.desc;
    if (liveCountdownEl) liveCountdownEl.textContent = current.timeUntilNext;

    // 2. Week Delivery Progress (25.0 год норматив: 20 будні + 5 субота)
    const week = slotsData.week;
    const weekEarningsEl = document.getElementById('slots-week-earnings');
    const weekRateEl = document.getElementById('slots-week-rate');

    syncDeliveryWidgetFromPlanner();

    if (weekEarningsEl) weekEarningsEl.textContent = `${Math.round(week.delivery_earnings).toLocaleString('uk-UA')} грн`;
    if (weekRateEl) {
        const rate = Math.round(week.avg_rate);
        weekRateEl.textContent = rate > 0 ? `${rate} грн/год` : `ціль ~${week.target_rate} грн/год`;
        if (rate >= week.target_rate) {
            weekRateEl.style.color = 'var(--color-success)';
        } else if (rate > 0) {
            weekRateEl.style.color = 'var(--color-warning)';
        }
    }

    // 3. Month Financial Progress (Target: $1 000 за курсом USD тільки в тисячах)
    const month = slotsData.month;
    const targetIncome = month.target_income || 45000;
    const monthTotalEl = document.getElementById('slots-month-total');
    const monthTargetEl = document.getElementById('slots-month-target');
    const monthFillEl = document.getElementById('slots-month-fill');
    const monthSchoolEl = document.getElementById('slots-month-school');
    const monthPumEl = document.getElementById('slots-month-pum');
    const monthDeliveryEl = document.getElementById('slots-month-delivery');
    const monthOtherEl = document.getElementById('slots-month-other');
    const monthOtherRow = document.getElementById('slots-month-other-row');
    const monthRemainingEl = document.getElementById('slots-month-remaining');
    const monthPctEl = document.getElementById('slots-month-pct');
    const headerSubEl = document.getElementById('slots-header-sub');

    const usdRate = slotsData?.settings?.usd_rate || 41.5;
    const targetUsd = Math.round(targetIncome / usdRate);
    const globalTargetUsd = 1000;
    const globalTargetUah = Math.round(globalTargetUsd * usdRate);
    const globalTargetPct = Math.min(100, Math.round(((month.total_income || 0) / globalTargetUah) * 100));

    if (headerSubEl) {
        if (targetUsd < 1000) {
            headerSubEl.innerHTML = `План: <strong>$${targetUsd.toLocaleString('uk-UA')}</strong> <small style="opacity:0.75;font-weight:normal">(~${Math.round(targetIncome).toLocaleString('uk-UA')} грн)</small> <span style="opacity:0.35;margin:0 4px;">·</span> <span class="slots-header-milestone" style="color:var(--color-success,#10b981);font-weight:600;" title="Загальна стратегічна ціль: $1 000 (~${globalTargetUah.toLocaleString('uk-UA')} грн) · Прогрес до цілі: ${globalTargetPct}%"><i class="ri-flag-2-fill"></i> Ціль: <strong>$1 000</strong></span>`;
            headerSubEl.title = `Поточний план: $${targetUsd.toLocaleString('uk-UA')} (~${Math.round(targetIncome).toLocaleString('uk-UA')} грн) · Стратегічна ціль: $1 000 (~${globalTargetUah.toLocaleString('uk-UA')} грн) · Доставка: 25.0 год/тижд (5 год СБ) · Вікно сну: 00:00–09:00`;
        } else {
            headerSubEl.innerHTML = `Ціль: <strong>$${targetUsd.toLocaleString('uk-UA')}</strong> <small style="opacity:0.75;font-weight:normal">(~${Math.round(targetIncome).toLocaleString('uk-UA')} грн)</small>`;
            headerSubEl.title = `Планка: $${targetUsd.toLocaleString('uk-UA')} (~${Math.round(targetIncome).toLocaleString('uk-UA')} грн) · Доставка: 25.0 год/тижд (5 год СБ) · Вікно сну: 00:00–09:00`;
        }
    }

    const globalBadgeEl = document.getElementById('slots-global-target-badge');
    if (globalBadgeEl) {
        globalBadgeEl.title = `Загальна стратегічна ціль: $1 000 (~${globalTargetUah.toLocaleString('uk-UA')} грн) · Прогрес до цілі: ${globalTargetPct}%`;
    }

    if (monthTotalEl) monthTotalEl.textContent = `${Math.round(month.total_income).toLocaleString('uk-UA')} грн`;
    if (monthTargetEl) monthTargetEl.textContent = `/ ${Math.round(targetIncome).toLocaleString('uk-UA')} грн (~$${targetUsd})`;
    if (monthPctEl) monthPctEl.textContent = `${month.progress_pct}%`;
    if (monthFillEl) monthFillEl.style.width = `${month.progress_pct}%`;

    const schoolPlan = month.school_base || slotsData?.settings?.school_base || 15500;
    const pumPlan = month.pum_base || slotsData?.settings?.pum_base || 6000;
    const delPlan = month.delivery_base || slotsData?.settings?.delivery_base || Math.max(0, targetIncome - schoolPlan - pumPlan);

    const formatPlanHint = (val: number) => {
        if (!val || val <= 0) return '';
        const kVal = val >= 1000 
            ? (val % 1000 === 0 ? `${val / 1000}к` : `${(val / 1000).toFixed(1).replace(/\.0$/, '')}к`)
            : `${val}`;
        return `(план ~${kVal})`;
    };

    // Ліцей: факт з доходів або 0 грн (з підказкою плану)
    if (monthSchoolEl) {
        const schoolVal = month.school_actual ?? 0;
        const planHint = `<span class="slots-plan-hint">${formatPlanHint(schoolPlan)}</span>`;
        if (schoolVal > 0) {
            monthSchoolEl.innerHTML = `${Math.round(schoolVal).toLocaleString('uk-UA')} грн ${planHint}`;
            monthSchoolEl.style.color = 'var(--color-success, #10b981)';
        } else {
            monthSchoolEl.innerHTML = `0 грн ${planHint}`;
            monthSchoolEl.style.color = '';
        }
    }

    // ПУМ: факт з доходів або 0 грн (з підказкою плану)
    if (monthPumEl) {
        const pumVal = month.pum_actual ?? 0;
        const planHint = `<span class="slots-plan-hint">${formatPlanHint(pumPlan)}</span>`;
        if (pumVal > 0) {
            monthPumEl.innerHTML = `${Math.round(pumVal).toLocaleString('uk-UA')} грн ${planHint}`;
            monthPumEl.style.color = 'var(--color-success, #10b981)';
        } else {
            monthPumEl.innerHTML = `0 грн ${planHint}`;
            monthPumEl.style.color = '';
        }
    }

    // Доставка
    if (monthDeliveryEl) {
        const delVal = month.delivery_earnings ?? 0;
        const planHint = `<span class="slots-plan-hint">${formatPlanHint(delPlan)}</span>`;
        if (delVal > 0) {
            monthDeliveryEl.innerHTML = `${Math.round(delVal).toLocaleString('uk-UA')} грн ${planHint}`;
            monthDeliveryEl.style.color = 'var(--brand-orange, #f97316)';
        } else {
            monthDeliveryEl.innerHTML = `0 грн ${planHint}`;
            monthDeliveryEl.style.color = '';
        }
    }

    // Інші доходи з вкладки «Доходи»
    if (monthOtherRow && monthOtherEl) {
        const otherVal = month.other_income ?? 0;
        if (otherVal > 0) {
            monthOtherRow.style.display = 'flex';
            monthOtherEl.textContent = `${Math.round(otherVal).toLocaleString('uk-UA')} грн`;
        } else {
            monthOtherRow.style.display = 'none';
        }
    }

    if (monthRemainingEl) {
        if (month.remaining <= 0) {
            monthRemainingEl.innerHTML = '<span style="color:var(--color-success,#10b981);font-weight:700;">Виконано! 🎉</span>';
        } else {
            monthRemainingEl.textContent = `Залишок: ${Math.round(month.remaining).toLocaleString('uk-UA')} грн`;
        }
    }

    // 4. Fill Today Report Inputs (if not already modified by user)
    const today = slotsData.today;
    const dateInput = document.getElementById('slot-input-date') as HTMLInputElement | null;
    const pumInput = document.getElementById('slot-input-pum') as HTMLInputElement | null;
    const schoolInput = document.getElementById('slot-input-school') as HTMLInputElement | null;
    const deliveryHoursInput = document.getElementById('slot-input-delivery-hours') as HTMLInputElement | null;
    const deliveryEarningsInput = document.getElementById('slot-input-delivery-earnings') as HTMLInputElement | null;
    const sleepCheck = document.getElementById('slot-input-sleep') as HTMLInputElement | null;
    const notesInput = document.getElementById('slot-input-notes') as HTMLInputElement | null;

    if (dateInput && !dateInput.value) dateInput.value = today.slot_date;
    if (pumInput && !pumInput.value) pumInput.value = String(today.pum_hours || 1.5);
    if (schoolInput && !schoolInput.value) schoolInput.value = String(today.school_hours || 4.5);
    if (deliveryHoursInput && !deliveryHoursInput.value) deliveryHoursInput.value = String(today.delivery_hours || 4.0);
    if (deliveryEarningsInput && !deliveryEarningsInput.value) deliveryEarningsInput.value = String(today.delivery_earnings || '');
    if (sleepCheck) sleepCheck.checked = today.sleep_maintained !== 0;
    if (notesInput && !notesInput.value) notesInput.value = today.notes || '';

    // 5. History Table
    renderHistoryTable(slotsData.history);

    // Sync planner timeline height with updated sidebar metrics
    if (typeof (window as any).syncTimelineHeightWithSidebar === 'function') {
        (window as any).syncTimelineHeightWithSidebar();
    }
}

function renderHistoryTable(history: SlotDayReport[]) {
    const tbody = document.getElementById('slots-history-tbody');
    if (!tbody) return;

    if (!history || history.length === 0) {
        tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;color:var(--text-muted);padding:20px;">Ще немає збережених записів</td></tr>';
        return;
    }

    tbody.innerHTML = history.map(row => {
        const rate = Number(row.delivery_hours) > 0 ? Math.round(Number(row.delivery_earnings) / Number(row.delivery_hours)) : 0;
        const sleepBadge = Number(row.sleep_maintained) === 1
            ? '<span class="slots-pill pill-green"><i class="ri-moon-line"></i> Дотримано</span>'
            : '<span class="slots-pill pill-red"><i class="ri-error-warning-line"></i> Зрив</span>';
        
        return `
            <tr>
                <td style="font-weight:600;color:var(--text-title);">${row.slot_date}</td>
                <td>${Number(row.pum_hours).toFixed(1)} год</td>
                <td>${Number(row.school_hours).toFixed(1)} год</td>
                <td><strong style="color:var(--brand-orange);">${Number(row.delivery_hours).toFixed(1)} год</strong></td>
                <td>${Math.round(Number(row.delivery_earnings)).toLocaleString('uk-UA')} грн</td>
                <td>${rate > 0 ? `${rate} грн/год` : '—'}</td>
                <td>${sleepBadge}</td>
            </tr>
        `;
    }).join('');
}

// Global functions for HTML interaction
(window as any).saveSlotsDailyReport = async function() {
    const dateInput = document.getElementById('slot-input-date') as HTMLInputElement | null;
    const pumInput = document.getElementById('slot-input-pum') as HTMLInputElement | null;
    const schoolInput = document.getElementById('slot-input-school') as HTMLInputElement | null;
    const deliveryHoursInput = document.getElementById('slot-input-delivery-hours') as HTMLInputElement | null;
    const deliveryEarningsInput = document.getElementById('slot-input-delivery-earnings') as HTMLInputElement | null;
    const sleepCheck = document.getElementById('slot-input-sleep') as HTMLInputElement | null;
    const notesInput = document.getElementById('slot-input-notes') as HTMLInputElement | null;

    const payload = {
        slot_date: dateInput ? dateInput.value : new Date().toISOString().split('T')[0],
        pum_hours: pumInput ? parseFloat(pumInput.value) || 0 : 1.5,
        school_hours: schoolInput ? parseFloat(schoolInput.value) || 0 : 4.5,
        delivery_hours: deliveryHoursInput ? parseFloat(deliveryHoursInput.value) || 0 : 0,
        delivery_earnings: deliveryEarningsInput ? parseFloat(deliveryEarningsInput.value) || 0 : 0,
        sleep_maintained: sleepCheck && sleepCheck.checked ? 1 : 0,
        notes: notesInput ? notesInput.value.trim() : ''
    };

    try {
        const res = await fetch('api/slots.php?action=save_day', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
            if ((window as any).showMessage) {
                (window as any).showMessage(data.message || 'Звіт дня збережено!', 'success');
            } else {
                alert('Звіт успішно збережено!');
            }
            await loadSlotsSummary();
        } else {
            throw new Error(data.error || 'Помилка збереження');
        }
    } catch (e: any) {
        console.error(e);
        if ((window as any).showMessage) {
            (window as any).showMessage(e.message || 'Не вдалося зберегти звіт', 'error');
        } else {
            alert('Помилка: ' + e.message);
        }
    }
};

(window as any).refreshSlotsData = loadSlotsSummary;

(window as any).openSlotsReportModal = function() {
    const modal = document.getElementById('slots-report-modal');
    if (modal) {
        modal.style.display = 'flex';
        setTimeout(() => {
            const firstInp = modal.querySelector('input') as HTMLInputElement | null;
            firstInp?.focus();
        }, 60);
    }
};

(window as any).closeSlotsReportModal = function() {
    const modal = document.getElementById('slots-report-modal');
    if (modal) modal.style.display = 'none';
};

(window as any).openSlotsHistoryModal = function() {
    const modal = document.getElementById('slots-history-modal');
    if (modal) modal.style.display = 'flex';
};

(window as any).closeSlotsHistoryModal = function() {
    const modal = document.getElementById('slots-history-modal');
    if (modal) modal.style.display = 'none';
};

const openSlotsSettingsModal = function() {
    const modal = document.getElementById('slots-settings-modal');
    if (!modal) return;

    const schoolInp = document.getElementById('slots-setting-school') as HTMLInputElement | null;
    const pumInp = document.getElementById('slots-setting-pum') as HTMLInputElement | null;
    const delInp = document.getElementById('slots-setting-delivery') as HTMLInputElement | null;
    const targetInp = document.getElementById('slots-setting-target') as HTMLInputElement | null;

    const currentSchool = slotsData?.settings?.school_base ?? slotsData?.month?.school_base ?? 15500;
    const currentPum = slotsData?.settings?.pum_base ?? slotsData?.month?.pum_base ?? 6000;
    const currentDel = slotsData?.settings?.delivery_base ?? slotsData?.month?.delivery_base ?? 23500;
    const currentTarget = slotsData?.settings?.target_income ?? slotsData?.month?.target_income ?? (currentSchool + currentPum + currentDel);

    if (schoolInp) schoolInp.value = String(Math.round(currentSchool));
    if (pumInp) pumInp.value = String(Math.round(currentPum));
    if (delInp) delInp.value = String(Math.round(currentDel));
    if (targetInp) targetInp.value = String(Math.round(currentTarget));

    const updateTargetUsdHint = () => {
        const hintEl = document.getElementById('slots-target-usd-hint');
        if (!hintEl || !targetInp) return;
        const val = parseFloat(targetInp.value) || 0;
        const rate = slotsData?.settings?.usd_rate || 41.5;
        if (val > 0) {
            hintEl.textContent = `(~$${Math.round(val / rate)})`;
        } else {
            hintEl.textContent = '';
        }
    };

    let targetManuallyEdited = false;
    if (targetInp) {
        targetInp.oninput = () => { 
            targetManuallyEdited = true; 
            updateTargetUsdHint();
        };
    }

    const autoCalcTarget = () => {
        if (targetManuallyEdited || !targetInp) return;
        const s = parseFloat(schoolInp?.value || '0') || 0;
        const p = parseFloat(pumInp?.value || '0') || 0;
        const d = parseFloat(delInp?.value || '0') || 0;
        if (s + p + d > 0) {
            targetInp.value = String(Math.round(s + p + d));
            updateTargetUsdHint();
        }
    };

    if (schoolInp) schoolInp.oninput = autoCalcTarget;
    if (pumInp) pumInp.oninput = autoCalcTarget;
    if (delInp) delInp.oninput = autoCalcTarget;

    // Збереження по Enter у будь-якому полі
    [schoolInp, pumInp, delInp, targetInp].forEach(inp => {
        if (inp && !(inp as any).__slotsEnterBound) {
            (inp as any).__slotsEnterBound = true;
            inp.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    saveSlotsSettings();
                }
            });
        }
    });

    updateTargetUsdHint();

    const milestoneUahHint = document.getElementById('slots-milestone-uah-hint');
    if (milestoneUahHint) {
        const rate = slotsData?.settings?.usd_rate || 41.5;
        milestoneUahHint.textContent = `(~${Math.round(1000 * rate).toLocaleString('uk-UA')} грн)`;
    }

    modal.style.display = 'flex';

    // Автофокус на першому полі з виділенням тексту для зручного редагування
    setTimeout(() => {
        if (schoolInp) {
            schoolInp.focus();
            schoolInp.select();
        }
    }, 60);
};

(window as any).setSlotsTargetTo1000 = function() {
    const targetInp = document.getElementById('slots-setting-target') as HTMLInputElement | null;
    const rate = slotsData?.settings?.usd_rate || 41.5;
    const val = Math.round(1000 * rate);
    if (targetInp) {
        targetInp.value = String(val);
        const hintEl = document.getElementById('slots-target-usd-hint');
        if (hintEl) hintEl.textContent = `(~$1 000)`;
    }
};

const closeSlotsSettingsModal = function() {
    const modal = document.getElementById('slots-settings-modal');
    if (modal) modal.style.display = 'none';
};

const saveSlotsSettings = async function() {
    const schoolInp = document.getElementById('slots-setting-school') as HTMLInputElement | null;
    const pumInp = document.getElementById('slots-setting-pum') as HTMLInputElement | null;
    const delInp = document.getElementById('slots-setting-delivery') as HTMLInputElement | null;
    const targetInp = document.getElementById('slots-setting-target') as HTMLInputElement | null;

    const payload: Record<string, number> = {};
    if (schoolInp && schoolInp.value !== '') payload.school_base = parseFloat(schoolInp.value);
    if (pumInp && pumInp.value !== '') payload.pum_base = parseFloat(pumInp.value);
    if (delInp && delInp.value !== '') payload.delivery_base = parseFloat(delInp.value);
    if (targetInp && targetInp.value !== '') payload.target_income = parseFloat(targetInp.value);

    try {
        const res = await fetch('api/slots.php?action=save_settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
            closeSlotsSettingsModal();
            if ((window as any).showMessage) {
                (window as any).showMessage(data.message || 'Плани успішно оновлено!', 'success');
            } else {
                alert('Плани успішно оновлено!');
            }
            await loadSlotsSummary();
        } else {
            throw new Error(data.error || 'Помилка збереження налаштувань');
        }
    } catch (e: any) {
        console.error(e);
        if ((window as any).showMessage) {
            (window as any).showMessage(e.message || 'Не вдалося зберегти плани', 'error');
        } else {
            alert('Помилка: ' + e.message);
        }
    }
};

(window as any).openSlotsSettingsModal = openSlotsSettingsModal;
(window as any).closeSlotsSettingsModal = closeSlotsSettingsModal;
(window as any).saveSlotsSettings = saveSlotsSettings;

(window as any).navigateToIncomes = function() {
    if (typeof (window as any).switchTab === 'function') {
        (window as any).switchTab('finances');
        setTimeout(() => {
            const incBtn = document.querySelector('#finances .sub-tab-button[data-sub-tab="incomes"]') as HTMLElement | null;
            if (incBtn) incBtn.click();
        }, 50);
    }
};

(window as any).loadSlotsSummary = loadSlotsSummary;

function setupSafeModalOverlay(modalId: string, closeFn: () => void) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    let isMousedownOnBackdrop = false;

    modal.addEventListener('mousedown', (e) => {
        isMousedownOnBackdrop = (e.target === modal);
    });

    modal.addEventListener('click', (e) => {
        // Закриваємо ТІЛЬКИ якщо і mousedown, і click відбулися безпосередньо на фоні оверлею.
        // Це унеможливлює випадкове закриття при виділенні тексту в інпуті мишкою або drag-select!
        if (isMousedownOnBackdrop && e.target === modal) {
            closeFn();
        }
        isMousedownOnBackdrop = false;
    });

    const card = modal.querySelector('.slots-modal-card');
    if (card) {
        card.addEventListener('mousedown', (e) => e.stopPropagation());
        card.addEventListener('click', (e) => e.stopPropagation());
    }
}

function initSlotsModalsSafeDismiss() {
    setupSafeModalOverlay('slots-settings-modal', closeSlotsSettingsModal);
    setupSafeModalOverlay('slots-report-modal', (window as any).closeSlotsReportModal);
    setupSafeModalOverlay('slots-history-modal', (window as any).closeSlotsHistoryModal);

    // Зручне закриття по Escape для модалок слотів
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            const settingsModal = document.getElementById('slots-settings-modal');
            if (settingsModal && settingsModal.style.display !== 'none') {
                closeSlotsSettingsModal();
                return;
            }
            const reportModal = document.getElementById('slots-report-modal');
            if (reportModal && reportModal.style.display !== 'none') {
                (window as any).closeSlotsReportModal?.();
                return;
            }
            const historyModal = document.getElementById('slots-history-modal');
            if (historyModal && historyModal.style.display !== 'none') {
                (window as any).closeSlotsHistoryModal?.();
                return;
            }
        }
    });
}

// Initialize on DOMContentLoaded and tab clicks
document.addEventListener('DOMContentLoaded', () => {
    loadSlotsSummary();
    initSlotsModalsSafeDismiss();

    // Auto-refresh live timeline every 30 seconds
    if (slotsInterval) clearInterval(slotsInterval);
    slotsInterval = setInterval(() => {
        if (slotsData) renderSlotsUI();
    }, 30000);

    // If slots tab is clicked, reload fresh data
    const slotsBtn = document.querySelector('[data-tab="slots"]');
    if (slotsBtn) {
        slotsBtn.addEventListener('click', () => {
            loadSlotsSummary();
        });
    }

    // Слухаємо мутації даних (створення/редагування/видалення доходів у вкладці «Доходи»)
    window.addEventListener('bb:data-mutated', () => {
        loadSlotsSummary();
    });
});

export { loadSlotsSummary };
